import React from 'react';
import { Link } from 'react-router-dom';
import { Infinity, Shield, Clock, CheckCircle, TrendingUp, Gift, Zap, Facebook, Twitter, Linkedin } from 'lucide-react';
import Navigation from '../../components/Navigation';
import { useTranslation } from 'react-i18next';

const Home = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }}>
      <Navigation />

      {/* Hero Section */}
      <section className="text-white py-24 pb-12 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #162440 0%, #456A9E 60%, #5A7AB0 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-xl md:text-2xl font-bold mb-6 leading-tight bg-white inline-block px-8 py-4 rounded-lg shadow-lg whitespace-nowrap uppercase" style={{ color: '#A8922A' }}>
              {t('home.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto" style={{ color: 'rgba(255,255,255,0.9)' }}>
              {t('home.hero.subtitle')}
            </p>
            <h2 className="text-xl md:text-2xl font-bold mb-6 leading-tight bg-white inline-block px-8 py-4 rounded-lg shadow-lg" style={{ color: '#A8922A' }}>
              {t('home.hero.title2')}
            </h2>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-10" style={{ backgroundColor: '#F8F7F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-md hover:shadow-lg transition-all">
              <h3 className="text-3xl font-bold mb-3" style={{ color: '#23395B' }}>100%</h3>
              <p className="text-xs text-gray-600">{t('home.stats.inflation')}</p>
            </div>
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-md hover:shadow-lg transition-all">
              <h3 className="text-3xl font-bold mb-3" style={{ color: '#23395B' }}>24/7</h3>
              <p className="text-xs text-gray-600">{t('home.stats.availability')}</p>
            </div>
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-md hover:shadow-lg transition-all">
              <div className="flex justify-center mb-3">
                <Infinity className="w-16 h-16" style={{ color: '#A8922A' }} />
              </div>
              <p className="text-xs text-gray-600">{t('home.stats.value')}</p>
            </div>
            <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-md hover:shadow-lg transition-all">
              <h3 className="text-3xl font-bold mb-3" style={{ color: '#23395B' }}>{t('home.stats.secure')}</h3>
              <p className="text-xs text-gray-600">{t('home.stats.blockchain')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Vouchers Explanation Section */}
      <section className="py-24" style={{ backgroundColor: '#F8F7F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-gray-900 mb-8">
                {t('home.vouchers.title')}
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>{t('home.vouchers.p1')}</p>
                <p>{t('home.vouchers.p2')}</p>
                <div className="p-6 rounded-r-lg border-l-4" style={{ backgroundColor: '#F5EBB9', borderLeftColor: '#A8922A' }}>
                  <p className="font-semibold" style={{ color: '#7A6A1E' }}>
                    {t('home.vouchers.tip')}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img
                  src="/assets/images/rocket_gen_img_110389a98-1766953663624-1770151566041.png"
                  alt="ValueBook digital voucher interface on mobile device"
                  className="w-80 h-96 object-cover rounded-3xl shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three Feature Cards Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              {t('home.platform.title')}
            </h2>
            <h3 className="text-3xl font-semibold mb-8" style={{ color: '#23395B' }}>
              {t('home.platform.subtitle')}
            </h3>
            <p className="text-xl text-gray-700 max-w-5xl mx-auto leading-relaxed">
              {t('home.platform.desc')}
            </p>
            <div className="mt-6 rounded-xl p-6 max-w-5xl mx-auto border-2" style={{ backgroundColor: '#F5EBB9', borderColor: '#CFB53B' }}>
              <p className="text-lg text-gray-800 italic">
                {t('home.platform.quote')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-2 border border-gray-100">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#A8922A' }}>
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                {t('home.platform.wallet')}
              </h4>
              <p className="text-gray-700 text-lg">
                {t('home.platform.walletDesc')}
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-2 border border-gray-100">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#A8922A' }}>
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                {t('home.platform.access')}
              </h4>
              <p className="text-4xl font-bold mb-2" style={{ color: '#A8922A' }}>24/7</p>
              <p className="text-gray-700 text-lg">
                {t('home.platform.accessDesc')}
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-2 border border-gray-100">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#23395B' }}>
                <Gift className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                {t('home.platform.issue')}
              </h4>
              <p className="text-gray-700 text-lg">
                {t('home.platform.issueDesc')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-2 border border-gray-100">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#23395B' }}>
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                {t('home.platform.storage')}
              </h4>
              <p className="text-gray-700 text-lg">
                {t('home.platform.storageDesc')}
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-xl transition-all transform hover:-translate-y-2 border border-gray-100">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: '#A8922A' }}>
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">
                {t('home.platform.exchange')}
              </h4>
              <p className="text-gray-700 text-lg">
                {t('home.platform.exchangeDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Value vs Price Comparison Section */}
      <section className="py-24" style={{ backgroundColor: '#F8F7F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              {t('home.valueVsPrice.title')}
            </h2>
            <h3 className="text-3xl font-semibold mb-8" style={{ color: '#23395B' }}>
              {t('home.valueVsPrice.subtitle')}
            </h3>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              {t('home.valueVsPrice.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-10 rounded-3xl shadow-md border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-gray-700" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">
                  {t('home.valueVsPrice.price')}
                </h4>
              </div>
              <p className="text-gray-600 mb-8 text-lg">{t('home.valueVsPrice.priceDesc')}</p>

              <div className="space-y-6">
                <div className="p-6 rounded-xl" style={{ backgroundColor: '#FAFAFA' }}>
                  <h5 className="font-bold text-gray-900 mb-3 text-lg">{t('home.valueVsPrice.declaredPrice')}</h5>
                  <p className="text-gray-700">{t('home.valueVsPrice.declaredPriceDesc')}</p>
                </div>
                <div className="p-6 rounded-xl" style={{ backgroundColor: '#FAFAFA' }}>
                  <h5 className="font-bold text-gray-900 mb-3 text-lg">{t('home.valueVsPrice.marketParity')}</h5>
                  <p className="text-gray-700">{t('home.valueVsPrice.marketParityDesc')}</p>
                </div>
                <div className="p-6 rounded-xl" style={{ backgroundColor: '#FAFAFA' }}>
                  <h5 className="font-bold text-lg text-gray-900">{t('home.valueVsPrice.changesOverTime')}</h5>
                </div>
              </div>
            </div>

            <div className="p-10 rounded-3xl shadow-md border-2 text-white" style={{ background: 'linear-gradient(135deg, #A8922A 0%, #CFB53B 50%, #E8D56A 100%)', borderColor: 'rgba(168,146,42,0.4)' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-2xl font-bold">
                  {t('home.valueVsPrice.value')}
                </h4>
              </div>
              <p className="mb-8 text-lg" style={{ color: 'rgba(255,255,255,0.9)' }}>{t('home.valueVsPrice.valueDesc')}</p>

              <div className="space-y-6">
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                  <h5 className="font-bold mb-3 text-lg">{t('home.valueVsPrice.fixedValue')}</h5>
                  <p style={{ color: 'rgba(255,255,255,0.85)' }}>{t('home.valueVsPrice.fixedValueDesc')}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                  <h5 className="font-bold mb-3 text-lg">{t('home.valueVsPrice.inflationProtection')}</h5>
                  <p style={{ color: 'rgba(255,255,255,0.85)' }}>{t('home.valueVsPrice.inflationProtectionDesc')}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
                  <h5 className="font-bold text-lg">{t('home.valueVsPrice.staysConstant')}</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inflation Example Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              {t('home.example.title')}
            </h2>
            <p className="text-2xl text-gray-700">
              {t('home.example.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <div className="p-10 rounded-3xl shadow-md border border-gray-200" style={{ backgroundColor: '#FAFAFA' }}>
              <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('home.example.today')}</h3>
              <div className="space-y-8">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <p className="text-gray-600 mb-3 text-lg">{t('home.example.money')}</p>
                  <p className="text-4xl font-bold text-gray-900">10.000 RSD</p>
                </div>
                <div className="p-6 rounded-xl shadow-sm border-2" style={{ backgroundColor: '#F5EBB9', borderColor: '#CFB53B' }}>
                  <p className="text-gray-600 mb-3 text-lg">{t('home.example.voucherValue')}</p>
                  <p className="text-4xl font-bold" style={{ color: '#A8922A' }}>{t('home.example.product')}</p>
                </div>
              </div>
            </div>

            <div className="p-10 rounded-3xl shadow-md border-2" style={{ backgroundColor: '#F5EBB9', borderColor: '#CFB53B' }}>
              <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('home.example.nextYear')}</h3>
              <div className="space-y-8">
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <p className="text-gray-600 mb-3 text-lg">{t('home.example.moneyInflation')}</p>
                  <div className="flex items-center gap-3">
                    <p className="text-4xl font-bold text-red-600 line-through">10.000 RSD</p>
                    <span className="text-red-600 text-2xl">✗</span>
                  </div>
                </div>
                <div className="p-6 rounded-xl shadow-sm border-2 bg-white" style={{ borderColor: '#A8922A' }}>
                  <p className="text-gray-600 mb-3 text-lg">{t('home.example.voucherValue')}</p>
                  <div className="flex items-center gap-3">
                    <p className="text-4xl font-bold" style={{ color: '#A8922A' }}>{t('home.example.product')}</p>
                    <CheckCircle className="w-10 h-10" style={{ color: '#A8922A' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem Workflow Section */}
      <section className="py-24" style={{ backgroundColor: '#F8F7F2' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              {t('home.ecosystem.title')}
            </h2>
            <h3 className="text-3xl font-semibold mb-8" style={{ color: '#23395B' }}>
              {t('home.ecosystem.subtitle')}
            </h3>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              {t('home.ecosystem.desc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[1,2,3,4]?.map((step) => (
              <div key={step} className="bg-white p-8 rounded-2xl shadow-md text-center hover:shadow-xl transition-all transform hover:-translate-y-2 border border-gray-100">
                <div className="w-20 h-20 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg" style={{ backgroundColor: '#A8922A' }}>
                  {step}
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-4">
                  {t(`home.ecosystem.step${step}`)}
                </h4>
                <p className="text-gray-700 text-lg">
                  {t(`home.ecosystem.step${step}Desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Benefits Section */}
      <section className="py-24" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-8" style={{ color: '#23395B' }}>
              {t('home.community.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-10 rounded-2xl border border-gray-200 shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#23395B' }}>
                <Gift className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold mb-4 text-center" style={{ color: '#23395B' }}>{t('home.community.loyalty')}</h4>
              <p className="text-lg text-center text-gray-600">{t('home.community.loyaltyDesc')}</p>
            </div>

            <div className="bg-white p-10 rounded-2xl border border-gray-200 shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#A8922A' }}>
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold mb-4 text-center" style={{ color: '#23395B' }}>{t('home.community.presale')}</h4>
              <p className="text-lg text-center text-gray-600">{t('home.community.presaleDesc')}</p>
            </div>

            <div className="bg-white p-10 rounded-2xl border border-gray-200 shadow-md hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#23395B' }}>
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-2xl font-bold mb-4 text-center" style={{ color: '#23395B' }}>{t('home.community.delivery')}</h4>
              <p className="text-lg text-center text-gray-600">{t('home.community.deliveryDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Analysis Table Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              {t('home.analysis.title')}
            </h2>
            <h3 className="text-3xl font-semibold mb-8" style={{ color: '#23395B' }}>
              {t('home.analysis.subtitle')}
            </h3>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto">
              {t('home.analysis.desc')}
            </p>
          </div>

          <div className="overflow-x-auto mb-12">
            <table className="w-full bg-white shadow-md rounded-2xl overflow-hidden border border-gray-100">
              <thead className="text-white" style={{ background: 'linear-gradient(135deg, #456A9E 0%, #4A6AA0 100%)' }}>
                <tr>
                  <th className="px-8 py-6 text-left text-xl font-bold">{t('home.analysis.characteristic')}</th>
                  <th className="px-8 py-6 text-left text-xl font-bold">{t('home.analysis.voucher')}</th>
                  <th className="px-8 py-6 text-left text-xl font-bold">{t('home.analysis.money')}</th>
                  <th className="px-8 py-6 text-left text-xl font-bold">{t('home.analysis.crypto')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="hover:bg-amber-50 transition-colors">
                  <td className="px-8 py-6 font-bold text-gray-900 text-lg">{t('home.analysis.issuer')}</td>
                  <td className="px-8 py-6 text-gray-700 text-lg">{t('home.analysis.issuerVoucher')}</td>
                  <td className="px-8 py-6 text-gray-700 text-lg">{t('home.analysis.issuerMoney')}</td>
                  <td className="px-8 py-6 text-gray-700 text-lg">{t('home.analysis.issuerCrypto')}</td>
                </tr>
                <tr className="hover:bg-amber-50 transition-colors">
                  <td className="px-8 py-6 font-bold text-gray-900 text-lg">{t('home.analysis.coverage')}</td>
                  <td className="px-8 py-6 font-bold text-lg" style={{ color: '#A8922A' }}>{t('home.analysis.coverageVoucher')}</td>
                  <td className="px-8 py-6 text-gray-700 text-lg">{t('home.analysis.coverageMoney')}</td>
                  <td className="px-8 py-6 text-gray-700 text-lg">{t('home.analysis.coverageCrypto')}</td>
                </tr>
                <tr className="hover:bg-amber-50 transition-colors">
                  <td className="px-8 py-6 font-bold text-gray-900 text-lg">{t('home.analysis.inflation')}</td>
                  <td className="px-8 py-6 font-bold text-lg" style={{ color: '#A8922A' }}>{t('home.analysis.inflationVoucher')}</td>
                  <td className="px-8 py-6 text-red-600 font-semibold text-lg">{t('home.analysis.inflationMoney')}</td>
                  <td className="px-8 py-6 text-red-600 font-semibold text-lg">{t('home.analysis.inflationCrypto')}</td>
                </tr>
                <tr className="hover:bg-amber-50 transition-colors">
                  <td className="px-8 py-6 font-bold text-gray-900 text-lg">{t('home.analysis.trust')}</td>
                  <td className="px-8 py-6 font-bold text-lg" style={{ color: '#A8922A' }}>{t('home.analysis.trustVoucher')}</td>
                  <td className="px-8 py-6 text-gray-700 text-lg">{t('home.analysis.trustMoney')}</td>
                  <td className="px-8 py-6 text-gray-700 text-lg">{t('home.analysis.trustCrypto')}</td>
                </tr>
                <tr className="hover:bg-amber-50 transition-colors">
                  <td className="px-8 py-6 font-bold text-gray-900 text-lg">{t('home.analysis.value')}</td>
                  <td className="px-8 py-6 font-bold text-lg" style={{ color: '#A8922A' }}>{t('home.analysis.valueVoucher')}</td>
                  <td className="px-8 py-6 text-gray-700 text-lg">{t('home.analysis.valueMoney')}</td>
                  <td className="px-8 py-6 text-gray-700 text-lg">{t('home.analysis.valueCrypto')}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 hover:shadow-lg transition-all">
              <h4 className="text-2xl font-bold text-gray-900 mb-4">{t('home.analysis.reliability')}</h4>
              <p className="text-gray-700 text-lg">{t('home.analysis.reliabilityDesc')}</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100 hover:shadow-lg transition-all">
              <h4 className="text-2xl font-bold text-gray-900 mb-4">{t('home.analysis.trustAssessment')}</h4>
              <p className="text-gray-700 text-lg">{t('home.analysis.trustAssessmentDesc')}</p>
            </div>
            <div className="p-8 rounded-2xl shadow-md border-2 hover:shadow-lg transition-all" style={{ backgroundColor: '#F5EBB9', borderColor: '#CFB53B' }}>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">{t('home.analysis.realCoverage')}</h4>
              <p className="text-gray-700 text-lg">{t('home.analysis.realCoverageDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-16" style={{ backgroundColor: '#23395B' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img
                  src="/assets/images/ZnaK_ValueBook_prilagodjen-1770155460741.png"
                  alt="ValueBook Trademark Logo"
                  className="w-12 h-12 object-contain"
                />
                <div className="flex flex-col items-center">
                  <h3 className="text-2xl font-bold" style={{ color: '#CFB53B' }}>ValueBook</h3>
                  <p className="text-sm" style={{ color: '#A8922A' }}>Marketplace</p>
                </div>
              </div>
              <p className="mb-6 leading-relaxed" style={{ color: '#D6E0EE' }}>
                {t('home.footer.tagline')}
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-12 h-12 rounded-full flex items-center justify-center transition-colors" style={{ backgroundColor: '#A8922A' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#7A6A1E'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#A8922A'}>
                  <Facebook className="w-6 h-6" />
                </a>
                <a href="#" className="w-12 h-12 rounded-full flex items-center justify-center transition-colors" style={{ backgroundColor: '#A8922A' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#7A6A1E'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#A8922A'}>
                  <Twitter className="w-6 h-6" />
                </a>
                <a href="#" className="w-12 h-12 rounded-full flex items-center justify-center transition-colors" style={{ backgroundColor: '#A8922A' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#7A6A1E'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#A8922A'}>
                  <Linkedin className="w-6 h-6" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-6 text-xl" style={{ color: '#CFB53B' }}>{t('home.footer.quickLinks')}</h4>
              <ul className="space-y-3" style={{ color: '#D6E0EE' }}>
                <li><Link to="/zapocni-trgovinu" className="hover:text-white transition-colors text-lg">{t('home.footer.trading')}</Link></li>
                <li><Link to="/objavi-ponudu-form" className="hover:text-white transition-colors text-lg">{t('home.footer.postOffer')}</Link></li>
                <li><Link to="/zajednica" className="hover:text-white transition-colors text-lg">{t('home.footer.community')}</Link></li>
                <li><Link to="/profil" className="hover:text-white transition-colors text-lg">{t('home.footer.myProfile')}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-6 text-xl" style={{ color: '#CFB53B' }}>{t('home.footer.support')}</h4>
              <ul className="space-y-3" style={{ color: '#D6E0EE' }}>
                <li><Link to="/uputstva" className="hover:text-white transition-colors text-lg">{t('home.footer.instructions')}</Link></li>
                <li><Link to="/uslovi-koriscenja" className="hover:text-white transition-colors text-lg">{t('home.footer.terms')}</Link></li>
                <li><Link to="/privatnost" className="hover:text-white transition-colors text-lg">{t('home.footer.privacy')}</Link></li>
                <li><Link to="/kontakt" className="hover:text-white transition-colors text-lg">{t('home.footer.contactLink')}</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-6 text-xl" style={{ color: '#CFB53B' }}>{t('home.footer.contact')}</h4>
              <p className="leading-relaxed text-lg" style={{ color: '#D6E0EE' }}>
                {t('home.footer.contactDesc')}
              </p>
            </div>
          </div>

          <div className="border-t pt-8 text-center" style={{ borderTopColor: 'rgba(168,146,42,0.3)' }}>
            <p className="text-lg" style={{ color: '#94a3b8' }}>
              {t('home.footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;